import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Confirm, Dropdown, Form, Grid, Icon, Input, Label, Message, Radio, Segment } from "semantic-ui-react";
import { Config } from "../../../config/api";
import PixelsService from "../../../services/pixels";
import ProductSearchDropdown from "./ProductSearchDropdown";
import SelectedSkusPanel from "./SelectedSkusPanel";

const getBrandsNoResultsMessage = (brandsLoading, brandSearchQuery, intl) => {
  if (brandsLoading) {
    return (
      <span style={{ display: "block", textAlign: "center", color: "#888", fontSize: "13px" }}>
        <Icon
          name="circle notch"
          loading
        />{" "}
        {intl.formatMessage({ id: "LABEL_SEARCHING_BRANDS", defaultMessage: "Searching brands…" })}
      </span>
    );
  }
  if (brandSearchQuery.length >= 3) {
    return (
      <span style={{ display: "block", textAlign: "center" }}>
        {intl.formatMessage(
          { id: "LABEL_NO_BRANDS_FOUND", defaultMessage: "No brands found for '{search}'" },
          { search: brandSearchQuery },
        )}
      </span>
    );
  }
  return (
    <span style={{ display: "block", textAlign: "center" }}>
      {brandSearchQuery.length > 0
        ? intl.formatMessage({
            id: "HINT_MIN_CHARS_TO_SEARCH",
            defaultMessage: "Type at least 3 characters to search...",
          })
        : intl.formatMessage({ id: "PLACEHOLDER_START_TYPING", defaultMessage: "Start typing to search..." })}
    </span>
  );
};

const BrandSkuPicker = ({
  attribution_level,
  onAttributionLevelChange,
  attributionLevelLabel,
  brand_names,
  onBrandNamesChange,
  initialBrandOptions = [],
  advertiser_id,
  seller_id,
  agency,
  disabled = false,
  brandDisabled = false,
  errors = {},
  selectedSkus,
  setSelectedSkus,
  helpText,
}) => {
  const intl = useIntl();
  const service = useRef(new PixelsService());

  const brandQuery = useRef("");
  const brandTimer = useRef(0);
  const productTimer = useRef(0);
  const productSearchRef = useRef(null);

  const [brandOptions, setBrandOptions] = useState(initialBrandOptions);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [productSearch, setProductSearch] = useState({ query: "", results: [], loading: false, showResults: false });
  const [hoveredSkuId, setHoveredSkuId] = useState(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (productSearchRef.current && !productSearchRef.current.contains(e.target)) {
        setProductSearch((prev) => ({ ...prev, showResults: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBrandsSearch = (_e, data) => {
    const prevQuery = brandQuery.current || "";
    brandQuery.current = data.searchQuery;
    setBrandSearchQuery(data.searchQuery);

    if (brandTimer.current) clearTimeout(brandTimer.current);
    if (brandQuery.current === prevQuery || brandQuery.current.length < 1) return;
    if (brandQuery.current.length < 3) {
      setBrandsLoading(false);
      return;
    }

    setBrandsLoading(true);

    brandTimer.current = setTimeout(async () => {
      try {
        const brands = await service.current.getBrands({
          // biome-ignore lint/style/useNamingConvention: API parameter
          brand_search_string: brandQuery.current,
          seller_id,
          // biome-ignore lint/style/useNamingConvention: API parameter
          apply_permissions: true,
          advertiser_id,
          // biome-ignore lint/style/useNamingConvention: API parameter
          agency_id: agency,
        });
        const results = brands.data.map((item) => ({ text: item.brand_name, value: item.brand_name }));
        const combinedBrands = [
          ...results,
          ...brand_names
            .filter((brand) => !brands.data.some((item) => item.brand_name === brand))
            .map((brand) => ({ text: brand, value: brand })),
        ];
        setBrandOptions(combinedBrands);
        setBrandsLoading(false);
      } catch (e) {
        console.error(e);
        setBrandsLoading(false);
      }
    }, Config.search_debounce_delay);
  };

  const handleProductSearch = (e) => {
    const newQuery = e.target.value;

    if (productTimer.current) clearTimeout(productTimer.current);

    if (newQuery.length === 0) {
      setProductSearch({ query: "", results: [], loading: false, showResults: false });
      return;
    }
    if (newQuery.length < 3) {
      setProductSearch((prev) => ({ ...prev, query: newQuery, showResults: true, loading: false, results: [] }));
      return;
    }

    setProductSearch((prev) => ({ ...prev, query: newQuery, showResults: true, loading: true }));

    productTimer.current = setTimeout(async () => {
      try {
        const res = await service.current.searchProducts({
          // biome-ignore lint/style/useNamingConvention: API parameter
          product_name: newQuery,
          seller_id,
          // biome-ignore lint/style/useNamingConvention: API parameter
          brand_name: brand_names,
          advertiser_id,
          // biome-ignore lint/style/useNamingConvention: API parameter
          agency_id: agency,
          // biome-ignore lint/style/useNamingConvention: API parameter
          apply_permissions: true,
        });
        setProductSearch((prev) => ({ ...prev, results: res.data || [], loading: false, showResults: true }));
      } catch (err) {
        console.error(err);
        setProductSearch((prev) => ({ ...prev, results: [], loading: false }));
      }
    }, Config.search_debounce_delay);
  };

  const toggleSkuSelection = (item) => {
    setSelectedSkus((prev) => {
      const exists = prev.some((s) => s.sku_id === item.sku_id);
      return exists ? prev.filter((s) => s.sku_id !== item.sku_id) : [...prev, item];
    });
  };

  const selectAllSearchResults = () => {
    setSelectedSkus((prev) => {
      const newItems = productSearch.results.filter((item) => !prev.some((s) => s.sku_id === item.sku_id));
      return [...prev, ...newItems];
    });
  };

  const brandsNoResultsMessage = getBrandsNoResultsMessage(brandsLoading, brandSearchQuery, intl);

  return (
    <Segment
      secondary
      padded
      style={{ fontSize: "inherit" }}
    >
      {helpText && <p style={{ fontSize: "13px", color: "#555", marginBottom: "16px" }}>{helpText}</p>}
      <Grid stackable>
        <Grid.Row columns={2}>
          <Grid.Column
            width={8}
            mobile={16}
            tablet={8}
            computer={8}
          >
            <Form.Field disabled={disabled}>
              <label>
                {attributionLevelLabel ||
                  intl.formatMessage({ id: "LABEL_ATTRIBUTION_LEVEL", defaultMessage: "Attribution Level" })}
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                <Radio
                  data-testid="retargeting-segment-attribution-brand"
                  name="attribution_level"
                  label={intl.formatMessage({ id: "LABEL_BRAND", defaultMessage: "Brand" })}
                  value="brand"
                  checked={attribution_level === "brand"}
                  onChange={onAttributionLevelChange}
                />
                <Radio
                  data-testid="retargeting-segment-attribution-sku"
                  name="attribution_level"
                  label={intl.formatMessage({ id: "LABEL_BRAND_ITEM_SKU", defaultMessage: "Brand & Item (SKU)" })}
                  value="sku"
                  checked={attribution_level === "sku"}
                  onChange={onAttributionLevelChange}
                />
              </div>
            </Form.Field>

            {attribution_level === "brand" && selectedSkus.length > 0 && (
              <Message
                info
                size="small"
                style={{ marginTop: "8px" }}
              >
                {intl.formatMessage({
                  id: "NOTICE_SKUS_RETAINED_BRAND_MODE",
                  defaultMessage:
                    "Your previously selected SKUs are retained but will not be used for attribution while in Brand mode. Switching back to Brand & Item (SKU) will restore your selections.",
                })}
              </Message>
            )}

            <Form.Field
              required
              disabled={disabled || brandDisabled}
              error={!!errors.brand_names}
            >
              <label>{intl.formatMessage({ id: "LABEL_BRANDS_CONTAINS", defaultMessage: "Brand Contains" })}</label>
              <div
                className="custom-error-container"
                style={{ width: "100%" }}
              >
                <Dropdown
                  data-testid="retargeting-segment-brand-names"
                  aria-label="Brand Contains"
                  required
                  search={(options) => options}
                  selection
                  multiple
                  options={brandOptions}
                  placeholder={intl.formatMessage({
                    id: "PLACEHOLDER_SELECT_BRAND",
                    defaultMessage: "Select brand(s)",
                  })}
                  noResultsMessage={brandsNoResultsMessage}
                  name="brand_names"
                  value={brand_names}
                  onSearchChange={handleBrandsSearch}
                  onClose={() => {
                    brandQuery.current = "";
                    setBrandSearchQuery("");
                  }}
                  onChange={(_, b) => onBrandNamesChange(b.value)}
                  style={{ width: "100%" }}
                />
                <div className="custom-error">{errors.brand_names}</div>
              </div>
            </Form.Field>

            {attribution_level === "sku" && (
              <Form.Field
                required
                disabled={disabled || brandDisabled}
              >
                <label>{intl.formatMessage({ id: "LABEL_SEARCH_PRODUCTS", defaultMessage: "Search Products" })}</label>
                <div
                  ref={productSearchRef}
                  style={{ position: "relative" }}
                >
                  <Input
                    fluid
                    icon={
                      productSearch.query ? (
                        <Icon
                          name="times circle"
                          link
                          onClick={() =>
                            setProductSearch({ query: "", results: [], loading: false, showResults: false })
                          }
                        />
                      ) : (
                        "search"
                      )
                    }
                    iconPosition={productSearch.query ? undefined : "left"}
                    placeholder={intl.formatMessage({
                      id: "PLACEHOLDER_SEARCH_PRODUCT_NAME_SKU",
                      defaultMessage: "Search by product name or sku…",
                    })}
                    value={productSearch.query}
                    disabled={disabled || brandDisabled}
                    onChange={handleProductSearch}
                    onFocus={() => {
                      if (productSearch.query.length > 0) setProductSearch((prev) => ({ ...prev, showResults: true }));
                    }}
                  />
                  <ProductSearchDropdown
                    productSearch={productSearch}
                    selectedSkus={selectedSkus}
                    hoveredSkuId={hoveredSkuId}
                    setHoveredSkuId={setHoveredSkuId}
                    toggleSkuSelection={toggleSkuSelection}
                    onSelectAll={selectAllSearchResults}
                  />
                </div>
              </Form.Field>
            )}
          </Grid.Column>

          <Grid.Column
            width={8}
            mobile={16}
            tablet={8}
            computer={8}
          >
            {attribution_level === "sku" && (
              <div>
                <Form.Field disabled={brandDisabled}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <label style={{ margin: 0 }}>
                      {intl.formatMessage({ id: "LABEL_SELECTED_SKUS", defaultMessage: "Selected SKUs" })}{" "}
                      <Label
                        circular
                        color="green"
                        size="mini"
                        style={{ position: "relative", top: "-1px" }}
                      >
                        {selectedSkus.length}
                      </Label>
                    </label>
                    {selectedSkus.length > 0 && !disabled && !brandDisabled && (
                      <Button
                        type="button"
                        basic
                        compact
                        size="mini"
                        color="red"
                        style={{ margin: 0 }}
                        onClick={() => setConfirmClearOpen(true)}
                      >
                        {intl.formatMessage({ id: "LABEL_CLEAR_ALL", defaultMessage: "Clear all" })}
                      </Button>
                    )}
                  </div>
                </Form.Field>
                <Confirm
                  open={confirmClearOpen}
                  header={intl.formatMessage({
                    id: "LABEL_CLEAR_ALL_CONFIRM_HEADER",
                    defaultMessage: "Clear all SKUs",
                  })}
                  content={intl.formatMessage(
                    {
                      id: "LABEL_CLEAR_ALL_CONFIRM_CONTENT",
                      defaultMessage: "This will remove all {count} selected SKU(s). Are you sure?",
                    },
                    { count: selectedSkus.length },
                  )}
                  confirmButton={intl.formatMessage({ id: "LABEL_CLEAR_ALL", defaultMessage: "Clear all" })}
                  cancelButton={intl.formatMessage({ id: "BTN_CANCEL", defaultMessage: "Cancel" })}
                  onConfirm={() => {
                    setSelectedSkus([]);
                    setConfirmClearOpen(false);
                  }}
                  onCancel={() => setConfirmClearOpen(false)}
                />
                <SelectedSkusPanel
                  selectedSkus={selectedSkus}
                  setSelectedSkus={setSelectedSkus}
                  skuError={errors.sku_ids}
                  readOnly={brandDisabled || disabled}
                />
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

export default BrandSkuPicker;
